import { useTranslation } from 'react-i18next';
import BigDoneIcon from 'assets/lottie/big-done-icon.json';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import Loader from 'components/Loader/Loader';
import Lottie from 'react-lottie-light';
import styles from './UploadCard.module.scss';
import TextInputEvent from 'hooks/eventCollector/events/TextInputEvent';
import useEventCollector from 'hooks/eventCollector/useEventCollector';

import React, {
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AnalyticsProps, NOT_TRACKED_CONTENT } from 'InternalLibrary/analytics';
import { Button, ButtonActor, Card, Text } from 'components';
import {
  CloseIcon,
  Document2Icon,
  DocumentErrorIcon,
  IdBackErrorIcon,
  IdBackIcon,
  IdFrontErrorIcon,
  IdFrontIcon,
  Trash2Icon,
  UploadCloudIcon,
} from 'components/icons';

import { FormikTouched } from 'formik';

const doneIconLottieOptions = {
  loop: false,
  autoplay: true,
  animationData: BigDoneIcon,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export const DOCUMENTS_TYPES_LIST = [
  'frontSideDni',
  'backSideDni',
  'bankReceipt',
  'idCard',
  'passport',
  'gunLicense',
  'driversLicense',
  'photo',
  'otherDocument',
  'incomeDocument',
];

export type DOC_TYPE = (typeof DOCUMENTS_TYPES_LIST)[number];

export type UploadCardProps = AnalyticsProps & {
  type: DOC_TYPE;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  className?: string;
  error?: boolean;
  isTouched?: boolean | FormikTouched<any> | FormikTouched<any>[];
  value?: File | null;
  onDelete: (name: string) => void;
  name: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  isNotInput?: boolean;
  innerRef?: any;
  title: string;
  warning?: string;
  onRemoveHandler: (name: string) => void;
  index: number;
};

export const SUPPORTED_FORMATS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/png',
];

export default memo(function UploadCard({
  type,
  value,
  onChange,
  onBlur,
  className,
  error,
  isTouched,
  onDelete,
  name,
  onClick,
  isNotInput = false,
  innerRef,
  title,
  analytics,
  onRemoveHandler,
  index,
  warning,
}: UploadCardProps) {
  const { t } = useTranslation('uploadCard');
  const [loading, setLoading] = useState(false);

  const [img, setImg] = useState('');

  const showError = isTouched && error;

  const Icon = useMemo(() => {
    const showError = isTouched && error;

    switch (type) {
      case 'frontSideDni':
        if (showError) return IdFrontErrorIcon;
        return IdFrontIcon;
      case 'backSideDni':
        if (showError) return IdBackErrorIcon;
        return IdBackIcon;
      case 'otherDocument':
        if (showError) return DocumentErrorIcon;
        return Document2Icon;
    }
    return showError ? DocumentErrorIcon : Document2Icon;
  }, [showError]);

  const { track } = useEventCollector();

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onChangeHandler = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true);
      await onChange(e);
      setLoading(false);

      if (analytics?.dontTrack) {
        return;
      }
      track(
        new TextInputEvent({
          description: analytics?.description,
          inputPlaceholder: name,
          value: analytics?.dontTrackContent
            ? NOT_TRACKED_CONTENT
            : String(analytics?.contentOverride) ?? e.target.files?.[0].name,
        }),
      );
    },
    [
      analytics?.contentOverride,
      analytics?.description,
      analytics?.dontTrack,
      analytics?.dontTrackContent,
      name,
      onChange,
      track,
    ],
  );

  useEffect(() => {
    const createImg = async () => {
      if (!value) return;

      let nextImg;
      if (value.type.includes('image')) {
        nextImg = URL.createObjectURL(value);
      } else {
        const newBlob = new Blob([value], { type: 'application/pdf' });
        nextImg = window.URL.createObjectURL(newBlob);
      }
      setImg(nextImg);
    };

    createImg();
  }, [value]);

  const getSizeFile = useCallback(() => {
    if (!value) return null;
    const bytes = Number((value.size / 1000).toFixed(2));
    if (bytes < 1000) return `${bytes} KB`;
    const megaBytes = (bytes / 1000).toFixed(2);
    return `${megaBytes} MB`;
  }, [value]);

  const handleDelete = useCallback(() => {
    setImg('');
    if (!onDelete) return;

    onDelete(name);
  }, [name, onDelete]);

  const handleRemoveDocument = useCallback(
    (name: string) => {
      onRemoveHandler(name);
    },
    [onRemoveHandler],
  );

  const Input = useMemo(() => {
    return isNotInput ? (
      <>
        <div
          onClick={onClick}
          className={styles.input}
          data-test={`uploadCard-input-${index}`}
        />
        <Icon className={styles.icon} data-test={`uploadCard-icon-${index}`} />
      </>
    ) : (
      <div data-test={`uploadCard-input-wrapper-${index}`}>
        <input
          data-test={`uploadCard-input-${index}`}
          onDrop={(e) => handleDrop(e)}
          ref={innerRef}
          name={name}
          className={classes(styles.input, loading && styles['loading-input'])}
          onChange={onChangeHandler}
          onBlur={onBlur}
          type="file"
          accept={SUPPORTED_FORMATS.join(',')}
          disabled={loading}
        />
        <Loader
          className={classes(styles.loader, loading && styles['loading-icon'])}
        />
        <Icon className={styles.icon} data-test={`uploadCard-icon-${index}`} />
        {type === 'otherDocument' && index > 2 && (
          <div className={styles['close-icon']}>
            <CloseIcon
              onClick={() => handleRemoveDocument(name)}
              data-test={`uploadCard-icon-close-${index}`}
            />
          </div>
        )}
      </div>
    );
  }, [
    isNotInput,
    onClick,
    Icon,
    innerRef,
    name,
    onChangeHandler,
    onBlur,
    type,
    index,
    handleRemoveDocument,
    loading,
  ]);

  const PreviewImage = useMemo(() => {
    if (!value) return null;

    const isImage = value.type.includes('image');
    const isVideo = value.type.includes('video');
    const isPdf = value.type === 'application/pdf';
    const style = !isPdf
      ? {
          backgroundImage: `url(${img}), url(${Document2Icon})`,
        }
      : {};
    return (
      <div className={styles.preview} style={style}>
        {isPdf && (
          <object aria-label="pdf-preview" data={img} type="application/pdf" />
        )}
        {isVideo && <video aria-label="video-preview" src={img} />}
        {isImage && <img aria-label="image-preview" src={img} />}
        {!showError && (
          <div className={styles['lottie-wrapper']}>
            <Lottie options={doneIconLottieOptions} />
          </div>
        )}
      </div>
    );
  }, [img, showError, value]);

  const customBorderError = showError && styles['custom-border-error'];

  return (
    <Card
      className={classes(
        styles.container,
        warning && styles['container-warning'],
        layout['full-width-border-box'],
        img
          ? styles['container-file']
          : styles[`container-empty${warning ? '-warning' : ''}`],
        customBorderError,
        className,
      )}
      dataTest={`uploadCard-card-${index}`}
    >
      {value ? PreviewImage : Input}
      <div
        className={styles.text}
        data-test={`uploadCard-card-wrapper-${index}`}
      >
        <Text
          className={classes(styles.label, showError && styles.error)}
          dataTest={`uploadCard-card-text-${index}`}
          text={title}
          kind="label"
          weight="medium"
        />
        {value && (
          <span
            className={classes(styles.subtitle, showError && styles.error)}
            data-test={`uploadCard-card-size-${index}`}
          >
            {getSizeFile()}
          </span>
        )}
      </div>

      {value ? (
        <ButtonActor
          analytics={{
            contentOverride: `${t('remove')} ${name}`,
            description: `Remove icon button: ${name}`,
          }}
          buttonProps={{
            className: classes(styles['remove-btn'], styles['card-button']),
            dataTest: `upload-card-trash-btn-${index}`,
            rightIcon: <Trash2Icon />,
            onClick: handleDelete,
            outlined: true,
            small: true,
          }}
          className={styles['remove-btn-wrapper']}
          width="fit-content"
        >
          <span>{t('remove')}</span>
        </ButtonActor>
      ) : (
        <Button
          dataTest={`upload-card-cloud-btn-${index}`}
          rightIcon={
            <UploadCloudIcon data-test={`upload-card-cloud-icon-${index}`} />
          }
          onClick={() => {}}
          outlined
          small
          className={classes(
            styles['upload-button'],
            styles['card-button'],
            loading && styles['loading-btn'],
          )}
          disabled={loading}
        >
          {!img && t('searchFile')}
        </Button>
      )}
    </Card>
  );
});
